import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getWarrantyConfig, updateWarrantyConfig } from '../api/admin.js';
import type { WarrantyConfigEntry } from '@m8-app/contracts/rest/warranty.js';

const PRODUCT_LABEL: Record<WarrantyConfigEntry['productType'], string> = {
  'stair-light':   'Stair Light',
  'ceiling-light': 'Ceiling Light',
  'cop-lop':       'COP-LOP (lift)',
};

const STARTS_FROM_LABEL: Record<WarrantyConfigEntry['startsFrom'], string> = {
  activation: 'device activation',
  registration: 'seller registration',
};

export function WarrantySettings() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['warranty-config'],
    queryFn: getWarrantyConfig,
  });

  return (
    <div className="p-8 max-w-2xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-1">Warranty Settings</h2>
      <p className="text-sm text-gray-500 mb-6">
        Set the default warranty duration per product. Changes apply only to devices registered or
        activated <em>after</em> the change — devices already in the field keep their existing expiry.
      </p>

      {isLoading && <div className="text-sm text-gray-500">Loading…</div>}
      {isError && <div className="text-sm text-red-600">Could not load warranty configuration.</div>}

      {data && (
        <div className="space-y-4">
          {data.entries.map((entry) => (
            <ConfigCard key={entry.productType} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

function ConfigCard({ entry }: { entry: WarrantyConfigEntry }) {
  const qc = useQueryClient();
  const [months, setMonths] = useState<number>(entry.durationMonths);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setMonths(entry.durationMonths);
  }, [entry.durationMonths]);

  const { mutate, isPending, error } = useMutation({
    mutationFn: () =>
      updateWarrantyConfig({ productType: entry.productType, durationMonths: months }),
    onSuccess: (fresh) => {
      qc.setQueryData(['warranty-config'], fresh);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    },
  });

  const dirty = months !== entry.durationMonths;
  const invalid = !Number.isInteger(months) || months < 1 || months > 240;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{PRODUCT_LABEL[entry.productType]}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Warranty starts from {STARTS_FROM_LABEL[entry.startsFrom]}.
          </p>
        </div>
        <span className="text-xs text-gray-400">
          Updated {new Date(entry.updatedAt).toLocaleDateString('en-GB')}
        </span>
      </div>

      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label
            htmlFor={`months-${entry.productType}`}
            className="block text-xs font-medium text-gray-700 mb-1"
          >
            Duration (months)
          </label>
          <input
            id={`months-${entry.productType}`}
            type="number"
            min={1}
            max={240}
            value={Number.isFinite(months) ? months : ''}
            onChange={(e) => setMonths(parseInt(e.target.value, 10))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          {invalid && (
            <p className="text-xs text-red-600 mt-1">Must be a whole number between 1 and 240.</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => mutate()}
          disabled={!dirty || invalid || isPending}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm rounded-lg font-medium"
        >
          {isPending ? 'Saving…' : saved ? 'Saved' : 'Save'}
        </button>
      </div>

      {error instanceof Error && (
        <p className="text-xs text-red-600 mt-2">{error.message}</p>
      )}
    </div>
  );
}
